import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { IVacationBidCrewEntitlementPreview } from "@vmosolutions/oc-interfaces";

import { VACATION_BID_TEST_ID } from "../testIds";

/** An entitlement edit waiting on the scheduler, with what it would change in the crew member's awards. */
export interface IEntitlementChange {
  name: string;
  fromDays: number;
  toDays: number;
  preview: IVacationBidCrewEntitlementPreview;
}

export interface IEntitlementChangeDialogProps {
  change: IEntitlementChange | null;
  onApply: () => void;
  onDaysOnly: () => void;
  onCancel: () => void;
}

const EntitlementChangeDialogStyles = {
  "& .change_intro": { marginBottom: "8px" },
  "& .change_label": { fontWeight: 700, margin: "10px 0 4px" },
  "& .change_line": { display: "flex", alignItems: "center", gap: "6px", marginLeft: "8px" },
  "& .over_capacity": { color: "warning.light" },
  "& .change_note": { marginTop: "10px", color: "text.secondary" },
};

/** Asks what to do with a crew member's awards when an edit to their days calls for changes to them. */
export const EntitlementChangeDialog = (props: IEntitlementChangeDialogProps): JSX.Element => {
  const { change, onApply, onDaysOnly, onCancel } = props;
  const adjustments = change?.preview.adjustments ?? [];
  const actions = change?.preview.actionsIfApplied ?? [];

  return (
    <Dialog
      open={!!change}
      onClose={onCancel}
      sx={EntitlementChangeDialogStyles}
      data-testid={VACATION_BID_TEST_ID.entitlementChangeDialog}
    >
      <DialogTitle>Change their awards too?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" className="change_intro">
          {`${change?.name ?? ""}'s days go from ${change?.fromDays ?? 0} to ${change?.toDays ?? 0}. ` +
            "Their awards need these changes to match:"}
        </Typography>
        {adjustments.map((adjustment) => (
          <Typography
            key={`${adjustment.kind} ${adjustment.slotId}`}
            variant="body2"
            className={`change_line${adjustment.overCapacity ? " over_capacity" : ""}`}
          >
            {adjustment.overCapacity && <WarningIcon fontSize="inherit" />}
            {adjustment.proposed}
          </Typography>
        ))}
        {actions.length > 0 && (
          <>
            <Typography variant="body2" className="change_label">
              Still to do once they are made
            </Typography>
            {actions.map((action) => (
              <Typography key={action} variant="body2" className="change_line">
                {action}
              </Typography>
            ))}
          </>
        )}
        <Typography variant="body2" className="change_note">
          Save days only changes none of their awards. Either way, a warning stays on them in Award Bids and View Bids
          until it is dismissed.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={onCancel}
          data-testid={VACATION_BID_TEST_ID.entitlementCancelButton}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={onDaysOnly}
          data-testid={VACATION_BID_TEST_ID.entitlementDaysOnlyButton}
        >
          Save days only
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={onApply}
          data-testid={VACATION_BID_TEST_ID.entitlementApplyButton}
        >
          Apply changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EntitlementChangeDialog;
